import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector, { CustomDetector } from 'i18next-browser-languagedetector';

import enTranslation from './locales/en/translation.json';
import trTranslation from './locales/tr/translation.json';

/**
 * Last-resort detector: when localStorage does not hold a language, resolve
 * to English (the app's default) before the 'en' fallbackLng applies.
 */
const defaultTrDetector: CustomDetector = {
    name: 'defaultTr',
    lookup: () => 'en',
    cacheUserLanguage: () => undefined,
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(defaultTrDetector);

i18n
    .use(languageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: enTranslation },
            tr: { translation: trTranslation },
        },
        supportedLngs: ['en', 'tr'],
        // Map regional codes ('tr-TR', 'en-US') onto their base language.
        nonExplicitSupportedLngs: true,
        fallbackLng: 'en',
        // Bundled resources make init complete synchronously (no async
        // backend), so there is no flash of untranslated keys on first paint.
        interpolation: {
            // React already escapes rendered strings.
            escapeValue: false,
        },
        react: {
            // The root of the app renders outside a Suspense boundary.
            useSuspense: false,
        },
        detection: {
            order: ['localStorage', 'defaultTr'],
            caches: ['localStorage'],
        },
        parseMissingKeyHandler: (key) => {
            // Safety net: an untranslated key must never surface as a raw
            // dotted path ("ui.ribbon.newTool"). De-camelCase the tail so a
            // missing key still renders as readable English.
            const tail = key.split('.').pop() ?? key;
            const words = tail
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/([A-Za-z])(\d)/g, '$1 $2')
                .replace(/(\d)([A-Za-z])/g, '$1 $2');
            return words.charAt(0).toUpperCase() + words.slice(1);
        },
    });

/**
 * The suite speaks ONE language: hand every language change in this document
 * — the top bar's menu, this app's own switchers and settings, the echo of a
 * suite broadcast — to the shared runtime, so the pick lands on the suite's
 * language plane and every application follows. The runtime ignores an echo
 * of its own resolved value, so the round trip stays loop-free.
 */
type SuiteLanguageRuntime = {
    language?: string;
    setLanguage?: (code: string) => void;
};

const rawChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = (lng, callback) => {
    const runtime = (window as unknown as { SuiteTheme?: SuiteLanguageRuntime })
        .SuiteTheme;
    if (lng && runtime && typeof runtime.setLanguage === 'function') {
        runtime.setLanguage(lng);
    }
    return rawChangeLanguage(lng, callback);
};

export default i18n;
